import { getCommanderClientHeaders } from '@commander/utils';
import axios, { type AxiosError, type AxiosInstance } from 'axios';

/**
 * Breadcrumb callback type for observability integration
 */
export type HttpBreadcrumbCallback = (
  method: string,
  url: string,
  statusCode?: number,
  isError?: boolean
) => void;

export interface CreateAxiosOptions {
  baseURL?: string;
  getToken?: () => string | null;
  onTokenExpired?: () => void;
  onUnauthorized?: () => Promise<void>;
  withCredentials?: boolean;
  /**
   * Callback for adding HTTP breadcrumbs for observability
   * Pass addHttpBreadcrumb from @commander/observability to enable request tracking
   * @example
   * ```ts
   * import { addHttpBreadcrumb } from '@commander/observability';
   * createAxiosInstance({ onHttpBreadcrumb: addHttpBreadcrumb });
   * ```
   */
  onHttpBreadcrumb?: HttpBreadcrumbCallback;
}

/**
 * Creates a configured axios instance with auth interceptors
 * Shared across all Commander apps for consistent auth behavior
 *
 * Uses dependency injection - no hard-coded browser APIs or storage
 *
 * @param options - Configuration options
 * @param options.baseURL - API base URL
 * @param options.getToken - Function to retrieve auth token (injected)
 * @param options.onTokenExpired - Callback when token expires
 * @param options.onUnauthorized - Callback when 401 received
 * @param options.withCredentials - Include credentials in requests
 * @returns Configured AxiosInstance with auth interceptors
 *
 * @example
 * ```typescript
 * const axiosInstance = createAxiosInstance({
 *   baseURL: import.meta.env.VITE_API_BASE_URL,
 *   getToken: () => localStorage.getItem('auth_token'),
 *   onTokenExpired: () => window.location.href = '/session-expired',
 * });
 * ```
 */
export function createAxiosInstance(options: CreateAxiosOptions = {}): AxiosInstance {
  const {
    baseURL = import.meta.env?.VITE_API_BASE_URL
      ? import.meta.env.VITE_API_BASE_URL
      : 'http://localhost:3011',
    getToken = () => null,
    withCredentials = true,
    onUnauthorized,
    onHttpBreadcrumb,
  } = options;

  // Create axios instance
  const instance = axios.create({
    baseURL,
    withCredentials,
  });

  // Request interceptor - add auth token to requests and track breadcrumbs
  instance.interceptors.request.use(
    (config) => {
      const token = getToken();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      config.headers.set(getCommanderClientHeaders());

      // Add breadcrumb for outgoing request
      if (onHttpBreadcrumb) {
        onHttpBreadcrumb(config.method || 'GET', config.url || '');
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle auth errors and track breadcrumbs
  instance.interceptors.response.use(
    (response) => {
      // Add breadcrumb for successful response
      if (onHttpBreadcrumb) {
        onHttpBreadcrumb(
          response.config.method || 'GET',
          response.config.url || '',
          response.status,
          false
        );
      }
      return response;
    },
    async (error: AxiosError) => {
      // Add breadcrumb for error response
      if (onHttpBreadcrumb) {
        onHttpBreadcrumb(
          error.config?.method || 'GET',
          error.config?.url || '',
          error.response?.status,
          true
        );
      }

      // Skip unauthorized handling for auth endpoints where 401 means
      // "invalid credentials", not "expired session"
      const requestUrl = error.config?.url || '';
      const isAuthEndpoint = /\/auth\/(login|signup|forgot-password|change-password|verify)/.test(
        requestUrl
      );

      // Handle 401 Unauthorized - only for non-auth endpoints (expired session, invalid token)
      if (error?.response?.status === 401 && onUnauthorized && !isAuthEndpoint) {
        await onUnauthorized();
        return Promise.reject(error);
      }

      // Handle UNAUTHORIZED message from backend
      const responseData = error?.response?.data as { message?: string } | undefined;
      if (responseData?.message === 'UNAUTHORIZED' && onUnauthorized && !isAuthEndpoint) {
        await onUnauthorized();
        return Promise.reject(error);
      }

      return Promise.reject(error);
    }
  );

  return instance;
}
